import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Avatar, {
  AvatarSize,
  AvatarStatus,
} from '../../../../components/Avatar';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import CheckboxGroup from '../../../../components/Checkbox/CheckboxGroup';
import GenericCards from '../../../../components/GenericCards';
import Link from '../../../../components/Link';
import profiles from '../../../../data/profiles/profiles.json';
import useModal from '../../../../hooks/useModal';
import { DateFormats, formatDate } from '../../../../utils/utils';
import RetireOnlineReviewModal from '../../RetireOnlineReviewModal';

export type Step8Props = {
  data: any;
  dataRetirementInfo: any;
  dataSelectPlan: any;
  dataBeneficiary: any;
  dataLumpSumDistribution: any;
  dataDirectDeposit: any;
  collectStepData: (data: any) => void;
  editPreviousStep: (step: number) => void;
};

const ReviewAndConfirmationStep = ({
  data,
  dataRetirementInfo,
  dataSelectPlan,
  dataBeneficiary,
  dataLumpSumDistribution,
  dataDirectDeposit,
  collectStepData,
  editPreviousStep,
}: Step8Props) => {
  const [currentStep, setCurrentStep] = useState<number>(0);

  const demoProfiles = profiles.data.filter((profile) => profile.id !== '1');

  const [
    showRetireOnlineReviewModal,
    handleShowRetireOnlineReviewModal,
    handleCloseRetireOnlineReviewModal,
  ] = useModal();

  const setStep = (step: number) => {
    setCurrentStep(step);
    handleShowRetireOnlineReviewModal();
  };

  const handleConfirmationCheckboxChange = (event: any, values: string[]) => {
    collectStepData({
      ...data,
      confirmationCheckBoxChecked: values[1],
    });
  };

  return (
    <>
      <RetireOnlineReviewModal
        showRetireOnlineReviewModal={showRetireOnlineReviewModal}
        handleCloseRetireOnlineReviewModal={handleCloseRetireOnlineReviewModal}
        editPreviousStep={() => editPreviousStep(currentStep)}
      />
      <section>
        <Row>
          <Col>
            <h5 className="mt-12 text-sm-20-lg-24">Review & Confirmation</h5>
          </Col>
        </Row>
        <Row className="mt-7 px-0">
          <Col>
            <span className="text-fs-6 text-ligther-grey lh-xs fw-light">
              Review to ensure you've entered everything correctly
            </span>
          </Col>
        </Row>
        <Row className="mt-7 ">
          {/* Information */}
          <Col className="col-12">
            <GenericCards variant="white">
              <Row>
                <Col className="fs-6 fw-light ps-lg-0 col-8">Information</Col>
                <Col className="text-end pe-lg-0 col-4">
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={light('pen-to-square')}
                    onClick={() => {
                      setStep(0);
                    }}
                  >
                    Edit
                  </Button>
                </Col>
              </Row>
              <Row className="mt-12 px-5 px-lg-0">
                <Col className="pe-lg-11">
                  <Row>
                    <Col className="text-fs-6 pb-3 ps-0">
                      Retirement Information
                    </Col>
                  </Row>
                  <Row className="text-fs-6 fw-light pt-3 lh-xs border-top">
                    <Col className="text-ligther-grey ps-0">
                      When is your last day of work?
                    </Col>
                    <Col className="text-end pe-0">
                      {formatDate(
                        dataRetirementInfo.retirementDate,
                        DateFormats.US
                      )}
                    </Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="text-ligther-grey ps-0">
                      When do you want to begin receiving pension benefits?
                    </Col>
                    <Col className=" text-end pe-0">
                      {formatDate(
                        dataRetirementInfo.benefitsStartDate,
                        DateFormats.US
                      )}
                    </Col>
                  </Row>
                </Col>
                <Col className=" col-12 col-lg-6 ps-lg-11">
                  <Row>
                    <Col className="text-fs-6  pb-3 ps-0">
                      Beneficiary Information
                    </Col>
                  </Row>
                  <Row className="text-fs-6 fw-light pt-3 lh-xs border-top">
                    <Col className="text-ligther-grey ps-0">Status</Col>
                    <Col className="text-end pe-0">
                      {dataRetirementInfo.martialStatus === 1
                        ? 'Not married'
                        : 'Married'}
                    </Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="text-ligther-grey ps-0">
                      Beneficiary Designation
                    </Col>
                    <Col className="text-end pe-0"></Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="text-ligther-grey ps-0">Spouse DOB</Col>
                    <Col className="text-end pe-0"></Col>
                  </Row>
                </Col>
              </Row>
            </GenericCards>
          </Col>
        </Row>
        {/* Selected options */}
        <Row className="mt-7">
          <Col className="col-12">
            <GenericCards variant="white">
              <Row>
                <Col className="fs-6 fw-light ps-lg-0 col-8">
                  Selected Options
                </Col>
                <Col className="text-end pe-lg-0 col-4">
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={light('pen-to-square')}
                    onClick={() => {
                      setStep(2);
                    }}
                  >
                    Edit
                  </Button>
                </Col>
              </Row>
              {/* Selected options */}
              <Row className="px-5 px-lg-0">
                <Col className=" col-12 col-lg-6 pe-lg-11">
                  <Row className="border-bottom">
                    <Col className="text-fs-6 pt-11 pb-3 ps-0">
                      Qualified Plan
                    </Col>
                    <Col className="text-caption pt-11 pb-3  text-ligther-grey fw-light text-end">
                      Benefit Amount
                    </Col>
                    <Col className="text-caption pt-11 pb-3 pe-0 text-ligther-grey fw-light text-end">
                      Beneficiary
                    </Col>
                  </Row>

                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="text-ligther-grey ps-0">Single Life</Col>
                    <Col className="text-end">$59,493.60</Col>
                    <Col className="text-end pe-0">N/A</Col>
                  </Row>
                </Col>
                <Col className=" col-12 col-lg-6 ps-lg-11">
                  <Row className="border-bottom ">
                    <Col className="text-fs-6 pt-11 pb-3 ps-0">
                      Non-Qualified Plan
                    </Col>
                    <Col className="text-caption pt-11 pb-3  text-ligther-grey fw-light text-end">
                      Benefit Amount
                    </Col>
                    <Col className="text-caption pt-11 pb-3 pe-0 text-ligther-grey fw-light text-end">
                      Beneficiary
                    </Col>
                  </Row>

                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="text-ligther-grey ps-0">
                      50% Joint & Survivor
                    </Col>
                    <Col className="text-end">$455.62</Col>
                    <Col className="text-end pe-0">$227.81</Col>
                  </Row>
                </Col>
              </Row>
              <Row className="px-5 px-lg-0">
                <Col className=" col-12 col-lg-6 pe-lg-11">
                  <Row className="border-bottom">
                    <Col className="text-fs-6 pt-11 pb-3 ps-0">
                      Cash Balance
                    </Col>
                    <Col className="text-caption pt-11 pb-3  text-ligther-grey fw-light text-end">
                      Benefit Amount
                    </Col>
                    <Col className="text-caption pt-11 pb-3 pe-0 text-ligther-grey fw-light text-end">
                      Beneficiary
                    </Col>
                  </Row>

                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="text-ligther-grey ps-0">
                      5 Year Certain & Continuous
                    </Col>
                    <Col className="text-end">$481.29</Col>
                    <Col className="text-end pe-0">$481.29</Col>
                  </Row>
                </Col>
                <Col className=" col-12 col-lg-6 ps-lg-11"></Col>
              </Row>
            </GenericCards>
          </Col>
        </Row>
        {/* Beneficiary */}
        <Row className="mt-7 ">
          <Col className="col-12">
            <GenericCards variant="white">
              <Row>
                <Col className="fs-6 fw-light ps-lg-0 col-8">Beneficiary</Col>
                <Col className="text-end pe-lg-0 col-4">
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={light('pen-to-square')}
                    onClick={() => {
                      setStep(3);
                    }}
                  >
                    Edit
                  </Button>
                </Col>
              </Row>
              <Row className="px-5 px-lg-0">
                <Col className="col-12 col-lg-6 pe-lg-11">
                  <Row className="border-bottom">
                    <Col className="text-fs-6 pt-11 pb-3 ps-0">Primary</Col>
                  </Row>

                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="ps-0">
                      <Row>
                        <Col className="col-4 col-lg-3">
                          <Avatar
                            id={'1'}
                            status={AvatarStatus.ENABLED}
                            size={AvatarSize.MEDIUM}
                            name={demoProfiles[0].name}
                          />
                        </Col>
                        <Col>
                          <div>{demoProfiles[0].name}</div>
                          <div className="text-ligther-grey">
                            {demoProfiles[0].relationship}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col className="text-end text-indigo pe-0">100%</Col>
                  </Row>
                </Col>
                <Col className="col-12 col-lg-6 ps-lg-11">
                  <Row className="border-bottom ">
                    <Col className="text-fs-6 pt-11 pb-3 ps-0">Contingent</Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="ps-0">
                      <Row>
                        <Col className="col-4 col-lg-3">
                          <Avatar
                            id={'2'}
                            status={AvatarStatus.ENABLED}
                            size={AvatarSize.MEDIUM}
                            name={demoProfiles[1].name}
                          />
                        </Col>
                        <Col>
                          <div>{demoProfiles[1].name}</div>
                          <div className="text-ligther-grey">
                            {demoProfiles[1].relationship}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col className="text-end text-indigo pe-0">50%</Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="ps-0">
                      <Row>
                        <Col className="col-4 col-lg-3 pe-0">
                          <Avatar
                            id={'3'}
                            status={AvatarStatus.ENABLED}
                            size={AvatarSize.MEDIUM}
                            name={demoProfiles[2].name}
                          />
                        </Col>
                        <Col>
                          <div>{demoProfiles[2].name}</div>
                          <div className="text-ligther-grey">
                            {demoProfiles[2].relationship}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col className="text-end text-indigo pe-0">50%</Col>
                  </Row>
                </Col>
              </Row>
            </GenericCards>
          </Col>
        </Row>
        {/* Direct deposit*/}
        <Row className="mt-7 ">
          <Col className="col-12">
            <GenericCards variant="white">
              <Row>
                <Col className="fs-6 fw-light ps-lg-0 col-8">
                  Direct Deposit
                </Col>
                <Col className="text-end pe-lg-0 col-4">
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={light('pen-to-square')}
                    onClick={() => {
                      setStep(5);
                    }}
                  >
                    Edit
                  </Button>
                </Col>
              </Row>
              <Row className="px-5 px-lg-0">
                <Col className="col-12 col-lg-8 pe-11">
                  <Row className="border-bottom">
                    <Col className="text-fs-6 pt-11 pb-3 ps-0">
                      Main Account
                    </Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="ps-0">
                      <Row>
                        <Col className="col-2 col-lg-1">
                          <Avatar
                            id={'4'}
                            status={AvatarStatus.ENABLED}
                            size={AvatarSize.MEDIUM}
                            name={'Bank of America'}
                          />
                        </Col>
                        <Col>
                          <div>Checking *****3412</div>
                          <div className="text-ligther-grey">
                            Bank of America
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </GenericCards>
          </Col>
        </Row>
        {/* Lump Sum Distribution */}
        <Row className="mt-7 ">
          <Col className="col-12">
            <GenericCards variant="white">
              <Row>
                <Col className="fs-6 fw-light ps-lg-0 col-8">
                  Lump Sum Distribution
                </Col>
                <Col className="text-end pe-lg-0 col-4">
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={light('pen-to-square')}
                    onClick={() => {
                      setStep(4);
                    }}
                  >
                    Edit
                  </Button>
                </Col>
              </Row>
              <Row className="px-5 px-lg-0">
                <Col className="col-12 col-lg-8 pe-11">
                  <Row className="border-bottom">
                    <Col className="text-fs-6 pt-11 pb-3 ps-0">
                      Rollover to Financial Institution - All
                    </Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-4">
                    <Col className="ps-0">
                      <Row>
                        <Col className="col-2 col-lg-1">
                          <Avatar
                            id={'5'}
                            status={AvatarStatus.ENABLED}
                            size={AvatarSize.MEDIUM}
                            name={'Bank of America'}
                          />
                        </Col>
                        <Col>
                          <div>Checking *****3412</div>
                          <div className="text-ligther-grey">
                            Bank of America
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </GenericCards>
          </Col>
        </Row>
        {/* Tax Withholding */}
        <Row className="mt-7 ">
          <Col className="col-12">
            <GenericCards variant="white">
              <Row>
                <Col className="fs-6 fw-light ps-lg-0 col-8">
                  Tax Withholding
                </Col>
                <Col className="text-end pe-lg-0 col-4">
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={light('pen-to-square')}
                    onClick={() => {
                      setStep(6);
                    }}
                  >
                    Edit
                  </Button>
                </Col>
              </Row>
              <Row className="px-5 px-lg-0">
                <Col className="col-12 pe-11">
                  <Row className="border-bottom">
                    <Col className="text-fs-6 pt-11 pb-3 ps-0">
                      Federal Tax Withholding
                    </Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-3">
                    <Col className="text-ligther-grey ps-0">Plan</Col>
                    <Col>Company Pension Plan</Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-3">
                    <Col className="text-ligther-grey ps-0">
                      How much you're withholding per check?
                    </Col>
                    <Col className="text-ligther-grey">
                      Calculate based on Head of Household, $45.00 Dependent
                      Credits, $5.00 Other Income, $4.00 Deductions, and $2.00
                      Extra Withholding.
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="px-5 px-lg-0">
                <Col className="col-12 pe-11">
                  <Row className="border-bottom">
                    <Col className="text-fs-6 pt-13 pb-3 ps-0">
                      State Tax Withholding
                    </Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-3">
                    <Col className="text-ligther-grey ps-0">Plan</Col>
                    <Col>Company Pension Plan</Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-3">
                    <Col className="text-ligther-grey ps-0">State</Col>
                    <Col>VA</Col>
                  </Row>
                  <Row className="text-fs-6 fw-light lh-xs pt-3">
                    <Col className="text-ligther-grey ps-0">
                      How much you're withholding per check?
                    </Col>
                    <Col className="text-ligther-grey">
                      Calculate based on Single with 2 allowances plus an
                      additional $33.00.
                    </Col>
                  </Row>
                </Col>
              </Row>
            </GenericCards>
            <Row className="pt-11">
              <Col>
                <CheckboxGroup
                  name="termsAndConditions"
                  value={[data.confirmationCheckBoxChecked]}
                  onChange={handleConfirmationCheckboxChange}
                >
                  <Checkbox
                    id={'confirm-step'}
                    value={'agree'}
                    variant="sm"
                    label={
                      'By checking this box, you indicate that you understand that you have chosen an option that requires Spousal Consent in order to be paid.'
                    }
                    className="mt-6"
                  />
                </CheckboxGroup>
              </Col>
            </Row>
            <Row>
              <Col className="ps-12 pt-11 fw-light lh-xs text-fs-6 col">
                Notice of Payment Options and Participant Rights - generally,
                pension plans may not pay benefits to you until certain notice
                requirements are satisfied. The plan must explain your rights
                and if you are married, the rights of your spouse. Need Help
                provides access to these materials including Relative Value,
                deferral rights, Special Tax Notice, etc.
              </Col>
            </Row>
            <Row>
              <Col className="ps-12 ">
                <Link
                  rightIcon={light('chevron-right')}
                  isBlue
                  className="pt-3"
                  href={`${
                    process.env.PUBLIC_URL
                  }/${'retirement-package-demo-example_v1.pdf'}`}
                  target={'_blank'}
                >
                  Deferring Receipt of Pension Benefits
                </Link>
                <Link
                  rightIcon={light('chevron-right')}
                  isBlue
                  className="pt-5"
                  href={`${
                    process.env.PUBLIC_URL
                  }/${'retirement-package-demo-example_v1.pdf'}`}
                  target={'_blank'}
                >
                  Payment Options
                </Link>
                <Link
                  rightIcon={light('chevron-right')}
                  isBlue
                  className="pt-5"
                  href={`${
                    process.env.PUBLIC_URL
                  }/${'retirement-package-demo-example_v1.pdf'}`}
                  target={'_blank'}
                >
                  Relative Value
                </Link>
                <Link
                  rightIcon={light('chevron-right')}
                  isBlue
                  className="pt-5"
                  href={`${
                    process.env.PUBLIC_URL
                  }/${'retirement-package-demo-example_v1.pdf'}`}
                  target={'_blank'}
                >
                  Special Tax Notice
                </Link>
                <Link
                  rightIcon={light('chevron-right')}
                  isBlue
                  className="pt-5"
                  href={`${
                    process.env.PUBLIC_URL
                  }/${'retirement-package-demo-example_v1.pdf'}`}
                  target={'_blank'}
                >
                  Understanding Your Optional Forms of Payment
                </Link>
              </Col>
            </Row>
          </Col>
        </Row>
      </section>
    </>
  );
};
export default ReviewAndConfirmationStep;
